
import { useState } from 'react';
import type { Tenant } from '../types';
import ConfirmModal from './ConfirmModal';

interface TenantTableProps {
  tenants: Tenant[];
  selectedIds: string[];
  onSelectAll: (checked: boolean) => void;
  onSelectOne: (id: string, checked: boolean) => void;
  onStatusChange: (id: string, status: 'active' | 'disabled') => void;
  onView: (tenant: Tenant) => void;
  onEdit: (tenant: Tenant) => void;
  onRemarkChange: (id: string, remark: string) => void;
}

export default function TenantTable({
  tenants,
  selectedIds,
  onSelectAll,
  onSelectOne,
  onStatusChange,
  onView,
  onEdit,
  onRemarkChange
}: TenantTableProps) {
  const [editingRemark, setEditingRemark] = useState<string | null>(null);
  const [remarkValue, setRemarkValue] = useState('');
  const [confirmModal, setConfirmModal] = useState<{
    show: boolean;
    tenantId: string;
    tenantName: string;
    action: 'enable' | 'disable';
  }>({ show: false, tenantId: '', tenantName: '', action: 'disable' });

  const handleStatusToggle = (tenant: Tenant) => {
    const newStatus = tenant.status === 'active' ? 'disabled' : 'active';
    if (newStatus === 'disabled') {
      setConfirmModal({
        show: true,
        tenantId: tenant.id,
        tenantName: tenant.name,
        action: 'disable'
      });
    } else {
      onStatusChange(tenant.id, newStatus);
    }
  };

  const handleConfirm = () => {
    onStatusChange(confirmModal.tenantId, confirmModal.action === 'disable' ? 'disabled' : 'active');
    setConfirmModal({ show: false, tenantId: '', tenantName: '', action: 'disable' });
  };

  const handleRemarkEdit = (tenant: Tenant) => {
    setEditingRemark(tenant.id);
    setRemarkValue(tenant.remark);
  };

  const handleRemarkSave = (id: string) => {
    onRemarkChange(id, remarkValue);
    setEditingRemark(null);
  };

  const getTypeLabel = (type: string) => {
    return type === 'enterprise' ? '企业' : '学校';
  };

  const allSelected = tenants.length > 0 && selectedIds.length === tenants.length;
  const someSelected = selectedIds.length > 0 && selectedIds.length < tenants.length;

  return (
    <>
      <div className="bg-white rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-3 text-left">
                  <input
                    type="checkbox"
                    checked={allSelected}
                    ref={(el) => {
                      if (el) el.indeterminate = someSelected;
                    }}
                    onChange={(e) => onSelectAll(e.target.checked)}
                    className="w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500 cursor-pointer"
                  />
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">租户 ID</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">名称</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">租户状态</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">租户类型</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">创建日期</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">管理员</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">更新日期</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">创建人</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">备注</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">操作</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {tenants.map((tenant) => (
                <tr key={tenant.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <input
                      type="checkbox"
                      checked={selectedIds.includes(tenant.id)}
                      onChange={(e) => onSelectOne(tenant.id, e.target.checked)}
                      className="w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500 cursor-pointer"
                    />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{tenant.id}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{tenant.name}</td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <button
                      onClick={() => handleStatusToggle(tenant)}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2 ${
                        tenant.status === 'active' ? 'bg-teal-600' : 'bg-gray-300'
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          tenant.status === 'active' ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      tenant.type === 'enterprise' 
                        ? 'bg-blue-100 text-blue-700' 
                        : 'bg-green-100 text-green-700'
                    }`}>
                      {getTypeLabel(tenant.type)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{tenant.createDate}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {tenant.adminName} | {tenant.adminUserId}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{tenant.updateDate}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{tenant.creator}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {editingRemark === tenant.id ? (
                      <div className="flex items-center gap-2">
                        <input
                          type="text"
                          value={remarkValue}
                          onChange={(e) => setRemarkValue(e.target.value)}
                          className="px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-teal-500"
                          autoFocus
                        />
                        <button
                          onClick={() => handleRemarkSave(tenant.id)}
                          className="text-teal-600 hover:text-teal-700"
                        >
                          <i className="ri-check-line"></i>
                        </button>
                        <button
                          onClick={() => setEditingRemark(null)}
                          className="text-gray-600 hover:text-gray-700"
                        >
                          <i className="ri-close-line"></i>
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <span className="text-gray-600">{tenant.remark || '-'}</span>
                        <button
                          onClick={() => handleRemarkEdit(tenant)}
                          className="text-gray-400 hover:text-teal-600"
                        >
                          <i className="ri-edit-line"></i>
                        </button>
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    <div className="flex items-center gap-3">
                      <button
                        onClick={() => onView(tenant)}
                        className="text-teal-600 hover:text-teal-700 whitespace-nowrap"
                      >
                        查看详情
                      </button>
                      <button
                        onClick={() => onEdit(tenant)}
                        className="text-blue-600 hover:text-blue-700 whitespace-nowrap"
                      >
                        编辑
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <ConfirmModal
        show={confirmModal.show}
        title="确认禁用"
        message={`确定要禁用租户"${confirmModal.tenantName}"吗？`}
        onConfirm={handleConfirm}
        onCancel={() => setConfirmModal({ show: false, tenantId: '', tenantName: '', action: 'disable' })}
      />
    </>
  );
}
